import React, { useState, useEffect } from "react";
import {
    Grid,
    Box,
    Typography,
    Container,
    useMediaQuery,
    GridItemsAlignment,
    Button, Breadcrumbs, Modal, Fade, Theme, Backdrop, CircularProgress
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Filter from "../../Components/Filter";
import {useSnackbar} from "notistack";
import AvaliableVetListItem from "../../Components/ListItems/AvaliableVet";
import API from "../../helpers/apiHelpers";
import moment from "moment";
import {specialities} from "../../Constants/specialities";
import {colorWhite} from "../../Constants/theme";
import Calendar from "../../Components/Calendar";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import {Link, useLocation} from "react-router-dom";
import {useSelector} from "react-redux";
import {RootState, useAppDispatch} from "../../redux/store";
import {updateServiceType} from "../../redux/scheduleAppointment/scheduling.actions";
import {setPrice} from "../../redux/scheduleAppointment/scheduling.reducers";
import {getAddress} from "../../redux/userProfile/userProfile.actions";

const useStyle = makeStyles((theme: Theme) => ({
    wrapper: {
        height: "100%",
        marginTop: 90,
        marginBottom: 50,
    },
    doctorList: {
        backgroundColor: colorWhite,
        width: "100%",
        padding: 10,
    },
    avatarPaper: {
        width: 150,
        height: 150
    },
    serviceType: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-around",
        width: 200,
        height: 30
    },
    serviceTypeContainer: {
        marginLeft: 40
    },
    servicePay: {
        height: 100,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center"
    },
    selectWidth: {
        height: 60,
        width: 145,
        fontSize: "18px",
        fontFamily: "NotoSerifDisplay"
    },
    avaliableVetRow: {
        marginBottom: 20,
    },
    modal: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        border: "none",
        [theme.breakpoints.down("sm")]: {
            width: "99%",
            margin: "0 auto"
        }
    },
    paper: {
        backgroundColor: theme.palette.background.paper,
        border: "none",
        boxShadow: theme.shadows[5],
        padding: theme.spacing(2, 2),
        display: "flex",
        flexDirection: "column"
    },
    button: {
        width: 110,
        marginTop: 10,
        marginBottom: 10
    }
}));

const AvaliableVet = () => {
    const classes = useStyle();
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const {state: {redirect}} = useLocation<{ redirect: boolean }>();
    const [vetProfiles, setVetProfiles] = useState([]);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
    const timeZone = moment.tz.guess();
    let isMobile = useMediaQuery("(min-width:600px)");
    let justifyResonsiveEnd: GridItemsAlignment = isMobile? "flex-end" : "center";
    let justifyResonsiveStart: GridItemsAlignment = isMobile? "flex-start" : "center";
    const schedulingReducer = useSelector((state: RootState) => state?.scheduleAppointment);
    const selectedPrice = useSelector((state: RootState) => state?.common.selectedPrice);
    const user = useSelector((state: RootState) => state?.user);
    const userProfile = useSelector((state: RootState) => state?.userProfile);
    const profileState = userProfile?.address?.state || userProfile?.stateOfResidence || null;
    const [showContactUs, setShowContactUs] = useState(false);
    const [showSpinner, setShowSpinner] = useState(true);

    useEffect(() => {
        if (user?.userId && !userProfile?.address?.state) {
            dispatch(getAddress(user.userId));
        }
    }, [dispatch, user?.userId, userProfile?.address?.state]);

    const getWorkingStates = () => (profileState ? [profileState] : null);

    const buildVetSearchParams = (day: Date) => {
        const date = moment(day).tz(timeZone, true);
        const param: Record<string, unknown> = {
            type: "VET",
            workingStates: schedulingReducer.serviceType === "RX_VIDEO" ? getWorkingStates() : null,
            specialties: [],
            clientTimeZone: timeZone,
            rangeBegins: date.format("YYYY-MM-DD"),
            rangeEnds: date.clone().add(1, "days").format("YYYY-MM-DD"),
        };
        if (schedulingReducer.reasonType === "PRESCRIPTION") {
            Object.assign(param, {
                workingStates: getWorkingStates(),
                prescriptionDesire: true,
            });
        }
        return param;
    };

    const appendPetSpecialty = async (param: Record<string, unknown>) => {
        const pet = await API.getAnimalById(schedulingReducer.selectedPetId);
        const petType = pet.type === "Other" ? pet.breed : pet.type;
        const filteredType = specialities.filter(speciality => speciality?.value === petType);
        param.specialties = filteredType.length > 0 ? [filteredType[0]?.value] : [petType || ""];
        return param;
    };

    const getTimeslotsForSelectedDate = (scheduling, dateTime) => {
        const dateTimeSlots = scheduling.find(item => moment(item.date).isSame(dateTime, "day"));
        return (dateTimeSlots || {timeslots: []}).timeslots;
    };

    const getSchedulingForUserTimezone = (schedule) => {
        let newSchedule = schedule
            .reduce((acc, item) => ([
                ...item.timeslots,
                ...acc,
            ]), [])
            .sort((a,b) => moment(a).diff(moment(b)))
            .map(item => {
                const tz = moment.tz.guess();
                const time = moment.tz(item, tz);
                return time;
            })
            .filter(timeslot => timeslot > moment())
            .reduce((acc, item) => {
                let key = item.format("YYYY-MM-DD");
                if (!acc[key]) {
                    acc[key] = {
                        date: key,
                        timeslots: []
                    };
                }
                acc[key].timeslots.push(item.format());
                return acc;
            }, {});
        return Object.values(newSchedule);
    };

    const getVetsForDate = async (day) => {
        setShowSpinner(true);
        setSelectedDate(day);
        try {
            const param = await appendPetSpecialty(buildVetSearchParams(day));
            const profiles = await API.getProfiles(param);
            setVetProfiles(profiles);
            setShowSpinner(false);
        } catch (err) {
            console.error(err);
            setShowSpinner(false);
        }
    };

    const handleChangeType = () => {
        let newType = schedulingReducer.serviceType === "ADVICE_CHAT" ? "ADVICE_VIDEO" : "ADVICE_CHAT";
        dispatch(updateServiceType(newType));
        dispatch(setPrice(selectedPrice));
    };
    useEffect(() => {
        if (redirect) enqueueSnackbar("The appointment time is no longer available. Please select a new time below. We apologize for any inconvenience!", {variant: "error"});
    },[]);
    useEffect(() => {
        if(vetProfiles.length) {
            let newTimeSlots = [];
            vetProfiles.forEach((profile) => {
                const schedulingForUserTimezone = getSchedulingForUserTimezone(profile.schedulingDays);
                const timeslots = getTimeslotsForSelectedDate(schedulingForUserTimezone, selectedDate);
                newTimeSlots.push(...timeslots);
            });
            (newTimeSlots.length) ? setAvailableTimeSlots(newTimeSlots) : setAvailableTimeSlots(newTimeSlots);
        }
    }, [vetProfiles]);

    useEffect(() => {
        (async () => {
            try {
                const param = await appendPetSpecialty(buildVetSearchParams(selectedDate));
                const profiles = await API.getProfiles(param);
                setVetProfiles(profiles);
                setShowSpinner(false);
            } catch (err) {
                console.error(err);
                setShowSpinner(false);
            }
        })();
    }, [schedulingReducer, profileState]);

    return (
        <Container maxWidth="lg">
            <Grid container spacing={1} className={classes.wrapper}>
                <Grid container className={classes.avaliableVetRow} alignItems="center">
                    <Grid item xs={11} sm={6} container justify={justifyResonsiveStart} style={{marginBottom: isMobile? 0 : 10}}>
                        <Breadcrumbs separator={<NavigateNextIcon fontSize="large" />} aria-label="breadcrumb">
                            <Link color="inherit" to="/schedule" style={{textDecoration: "none"}}>
                                <Typography variant="body2">Appointments</Typography>
                            </Link>
                            <Typography variant="body2">Book Now</Typography>
                        </Breadcrumbs>
                    </Grid>
                </Grid>
                <Calendar
                    value={selectedDate || new Date()}
                    handleChange={getVetsForDate}
                    loading={showSpinner}
                />
                <Grid item xs={12} container justify="center">
                    <Grid item xs={11} container justify="flex-start" style={{marginBottom: 40}}>
                        <Typography variant="h2">{moment(selectedDate).format("dddd, MMMM D, YYYY")}</Typography>
                    </Grid>
                </Grid>
                <Box marginBottom="20px" />
                {showSpinner &&
                    <div style={{
                        position: "relative",
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}>
                    <CircularProgress/>
                    </div>
			}
                {
                    (!vetProfiles.length || !availableTimeSlots.length) && !showSpinner ?
                        <Grid item xs={12} container justify="center" spacing={1}>
                            <Grid item xs={11} container justify="flex-start">
                                <Typography>No available vets found. Please message Support and we will organize an appointment for you.</Typography>
                            </Grid>
                            <Grid item container xs={11} spacing={1} justify="flex-start">
                                {schedulingReducer.reasonType !== "PRESCRIPTION" &&
									<Grid item xs={12} sm={4}>
									    <Button
                                            type="button"
									        variant="contained"
									        color="primary"
									        onClick={handleChangeType}
									    >
										Change Appointment to {schedulingReducer.serviceType === "ADVICE_CHAT" ? "Video" : "Chat"}
									    </Button>
									</Grid>
                                }
                            </Grid>
                        </Grid>
                        :
                        vetProfiles.map((profile) => {
                            const schedulingForUserTimezone = getSchedulingForUserTimezone(profile.schedulingDays);
                            const timeslots = getTimeslotsForSelectedDate(schedulingForUserTimezone, selectedDate);
                            return (
                                <AvaliableVetListItem
                                    name={`Dr. ${profile.lastName || ""}`}
                                    key={profile.id}
                                    profileId={profile.id}
                                    image={profile.image}
                                    userId={profile.userId}
                                    profile={profile}
                                    speciality={profile.speciality}
                                    scheduling={timeslots}
                                    redirect={redirect ? true : false}
                                />
                            );
                        })
                }
                <Modal
                    aria-labelledby="transition-modal-title"
                    aria-describedby="transition-modal-description"
                    className={classes.modal}
                    open={showContactUs}
                    onClose={() => setShowContactUs(false)}
                    closeAfterTransition
                    BackdropComponent={Backdrop}
                    BackdropProps={{
                        timeout: 500,
                    }}
                >
                    <Fade in={showContactUs}>
                        <div className={classes.paper}>
                            <Typography variant="h2" gutterBottom>
								Contact Us
                            </Typography>
                            <Typography variant="body2" gutterBottom>
								Hours: <span style={{fontWeight: 400}}>9am - midnight EST</span>
                            </Typography>
                            <Typography variant="body2" gutterBottom>
								Email: <a aria-label="email support at hello@thevideovets.com" href="mailto:hello@thevideovets.com" style={{fontWeight: 400, textDecoration: "none"}}>hello@thevideovets.com</a>
                            </Typography>
                            <Typography variant="body2" gutterBottom>
								Phone <span style={{fontWeight: 400}}>(Call or Text): <a aria-label="call or text at 9172590518" href="tel:9172590518" style={{fontWeight: 400, textDecoration: "none"}}>(917) 259 - 0518</a></span>
                            </Typography>
                            <Typography variant="body2" gutterBottom>
								Chat <span style={{fontWeight: 400}}>If you don't see a chat icon in the bottom right, please try refreshing your browser.</span>
                            </Typography>
                            <Button
                                type="button"
                                variant="outlined"
                                color="primary"
                                className={classes.button}
                                onClick={() => setShowContactUs(false)}
                            >
								Close
                            </Button>
                            <Typography variant="caption">
								In the case of a life threatening emergency,
                            </Typography>
                            <Typography variant="caption" gutterBottom>
								seek care at the closest emergency clinic.
                            </Typography>
                        </div>
                    </Fade>
                </Modal>
            </Grid>
        </Container>
    );
};

export default AvaliableVet;
